"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useLanguage } from "@/components/LanguageProvider";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { pickI18n } from "@/lib/planI18n";
import { unwrapApiData } from "@/lib/apiResponse";
import { APP_RESUME_EVENT } from "@/lib/appLifecycle";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

type BillingCycle = "mo" | "yr";

type Plan = {
  id: string;
  planId?: number;
  name: string;
  name_i18n?: Record<string, string> | null;
  description: string | null;
  description_i18n?: Record<string, string> | null;
  price: number;
  price_monthly?: number | null;
  price_yearly?: number | null;
  interval: string;
  max_tokens: number | null;
  storage_limit_bytes?: number | null;
  paas_enabled?: boolean;
  promotion_names?: string[];
  promotion_benefits?: {
    aiTokensBonus?: number;
    storageBytesBonus?: number;
    paasEnabled?: boolean;
  };
  is_active: boolean;
  sort_order?: number;
  planVersionId?: number | null;
  billingAvailable?: boolean;
  promo_monthly_name?: string | null;
  promo_monthly_name_i18n?: Record<string, string> | null;
  promo_monthly_discount_amount?: number | null;
  promo_monthly_start_at?: string | null;
  promo_monthly_end_at?: string | null;
  promo_yearly_name?: string | null;
  promo_yearly_name_i18n?: Record<string, string> | null;
  promo_yearly_discount_amount?: number | null;
  promo_yearly_start_at?: string | null;
  promo_yearly_end_at?: string | null;
};

function getPriceFor(plan: Plan, cycle: BillingCycle): number | null {
  if (cycle === "mo") {
    if (plan.price_monthly != null) return plan.price_monthly;
    if (plan.interval === "mo") return plan.price;
    return null;
  }
  if (plan.price_yearly != null) return plan.price_yearly;
  if (plan.interval === "yr") return plan.price;
  return null;
}

function computePromo(
  plan: Plan,
  cycle: BillingCycle,
  basePrice: number,
  uiLang: string,
): {
  active: boolean;
  discountedPrice: number;
  discountPercent: number;
  name: string | null;
} {
  const amount =
    cycle === "mo"
      ? Number(plan.promo_monthly_discount_amount) || 0
      : Number(plan.promo_yearly_discount_amount) || 0;
  const startAt =
    cycle === "mo" ? plan.promo_monthly_start_at : plan.promo_yearly_start_at;
  const endAt =
    cycle === "mo" ? plan.promo_monthly_end_at : plan.promo_yearly_end_at;
  const name =
    cycle === "mo"
      ? pickI18n(
          plan.promo_monthly_name_i18n ?? null,
          uiLang,
          plan.promo_monthly_name,
        ) || null
      : pickI18n(
          plan.promo_yearly_name_i18n ?? null,
          uiLang,
          plan.promo_yearly_name,
        ) || null;
  if (amount <= 0 || basePrice <= 0)
    return {
      active: false,
      discountedPrice: basePrice,
      discountPercent: 0,
      name: null,
    };
  const now = Date.now();
  const startOk = !startAt || new Date(startAt).getTime() <= now;
  const endOk = !endAt || new Date(endAt).getTime() >= now;
  if (!startOk || !endOk)
    return {
      active: false,
      discountedPrice: basePrice,
      discountPercent: 0,
      name: null,
    };
  return {
    active: true,
    discountedPrice: Math.max(0, basePrice - amount),
    discountPercent: Math.round((amount / basePrice) * 100),
    name,
  };
}

function computeYearlySavings(plan: Plan): number {
  const m = getPriceFor(plan, "mo");
  const y = getPriceFor(plan, "yr");
  if (!m || !y || m <= 0 || y <= 0) return 0;
  return Math.max(0, Math.round((1 - y / (m * 12)) * 100));
}

type PlanInfo = {
  plan: Plan | null;
  billingAvailable?: boolean;
  subscription_status?: string | null;
  cancel_at_period_end?: boolean;
  scheduledChange?: {
    planVersionId: number;
    effectiveAt: string | null;
    reason: string | null;
  } | null;
  billing_cycle?: BillingCycle;
  usage: {
    tokens_used_month: number;
    tokens_limit: number | null;
    period_start: string;
    period_end: string;
  } | null;
};

export function PlanCatalog({
  embedded = false,
  onBack,
}: {
  embedded?: boolean;
  onBack?: () => void;
}) {
  const { t, language } = useLanguage();
  const router = useRouter();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadRevision, setReloadRevision] = useState(0);
  const [cycle, setCycle] = useState<BillingCycle>("mo");
  const [changingPlanId, setChangingPlanId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoadError("");
        const [plansRes, meRes] = await Promise.all([
          fetchWithAuth(`${API_URL}/api/subscriptions/plans`),
          fetchWithAuth(`${API_URL}/api/users/me/plan`),
        ]);
        if (!plansRes.ok) {
          throw new Error(`플랜 조회 실패 (${plansRes.status})`);
        }
        if (plansRes.ok) {
          const data = unwrapApiData<{ plans?: Plan[] }>(await plansRes.json());
          if (!cancelled) {
            const list: Plan[] = (data.plans || []).filter(
              (p: Plan) => p.is_active,
            );
            list.sort(
              (a, b) =>
                (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.price - b.price,
            );
            setPlans(list);
          }
        }
        if (meRes.ok) {
          const info = unwrapApiData<PlanInfo>(await meRes.json());
          if (!cancelled) {
            setPlanInfo(info);
            // 유저의 현재 billing_cycle 로 토글 초기화
            if (info?.billing_cycle === "yr" || info?.billing_cycle === "mo") {
              setCycle(info.billing_cycle);
            }
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLoadError(
            "플랜 정보를 불러오지 못했습니다. 연결을 확인한 뒤 다시 시도해주세요.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadRevision]);

  useEffect(() => {
    const refreshPlans = () => setReloadRevision((revision) => revision + 1);
    window.addEventListener(APP_RESUME_EVENT, refreshPlans);
    return () => window.removeEventListener(APP_RESUME_EVENT, refreshPlans);
  }, []);

  const handleSelect = async (plan: Plan) => {
    const targetPrice = getPriceFor(plan, cycle);
    const currentPrice = planInfo?.plan
      ? getPriceFor(planInfo.plan, cycle)
      : null;
    const hasPaidSubscription = Boolean(planInfo?.subscription_status);

    if (targetPrice === 0 && hasPaidSubscription) {
      setChangingPlanId(plan.id);
      setActionMessage("");
      try {
        const response = await fetchWithAuth(
          `${API_URL}/api/subscriptions/me/cancel`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason: "플랜 화면에서 무료 전환 요청" }),
          },
        );
        if (!response.ok)
          throw new Error(`해지 예약 실패 (${response.status})`);
        setActionMessage("현재 이용 기간 종료 후 무료 플랜으로 전환됩니다.");
        setReloadRevision((revision) => revision + 1);
      } catch (error) {
        console.error(error);
        setActionMessage(
          "무료 플랜 전환을 예약하지 못했습니다. 다시 시도해주세요.",
        );
      } finally {
        setChangingPlanId(null);
      }
      return;
    }

    if (
      targetPrice != null &&
      currentPrice != null &&
      targetPrice > 0 &&
      targetPrice < currentPrice &&
      hasPaidSubscription
    ) {
      setChangingPlanId(plan.id);
      setActionMessage("");
      try {
        const response = await fetchWithAuth(
          `${API_URL}/api/subscriptions/me/schedule-downgrade`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              targetPlanId: Number(plan.planId ?? plan.id),
              reason: "플랜 화면에서 다운그레이드 요청",
            }),
          },
        );
        if (!response.ok)
          throw new Error(`다운그레이드 예약 실패 (${response.status})`);
        setActionMessage("다음 갱신일부터 선택한 플랜이 적용됩니다.");
        setReloadRevision((revision) => revision + 1);
      } catch (error) {
        console.error(error);
        setActionMessage(
          "다운그레이드를 예약하지 못했습니다. 다시 시도해주세요.",
        );
      } finally {
        setChangingPlanId(null);
      }
      return;
    }

    if (!plan.billingAvailable || !planInfo?.billingAvailable) return;
    const query = new URLSearchParams({
      planId: String(plan.planId ?? plan.id),
      billingPeriod: cycle === "yr" ? "YEARLY" : "MONTHLY",
    });
    router.push(`/payment/process?${query.toString()}`);
  };

  const currentPlanId = planInfo?.plan?.id ?? null;

  return (
    <div
      className={
        embedded
          ? "w-full bg-background"
          : "h-full w-full overflow-y-auto bg-background"
      }
    >
      <div
        className={
          embedded
            ? "flex w-full flex-col gap-4"
            : "mx-auto flex min-h-full w-full max-w-6xl flex-col gap-8 px-6 py-8 max-md:pt-[80px]"
        }
      >
        {/* Back + Header */}
        <div className={`flex flex-col shrink-0 ${embedded ? "gap-2.5" : "gap-4"}`}>
          <button
            type="button"
            onClick={() => (onBack ? onBack() : router.back())}
            className={`flex w-fit items-center font-bold text-gray-400 transition-colors hover:text-seexr-primary ${embedded ? "gap-1 text-[11px]" : "gap-1.5 text-[13px]"}`}
          >
            <svg
              width={embedded ? 12 : 14}
              height={embedded ? 12 : 14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {embedded ? "현재 플랜으로 돌아가기" : t("chat.cancel") || "뒤로"}
          </button>
          <div className={`flex flex-col ${embedded ? "gap-1" : "gap-2"}`}>
            <h1 className={`${embedded ? "text-[16px]" : "text-[28px]"} font-black text-gray-900 dark:text-gray-100 tracking-tight`}>
              {t("plan.page_title") || "플랜"}
            </h1>
            <p className={`${embedded ? "text-[10.5px] leading-relaxed" : "text-[14px]"} text-gray-500 dark:text-gray-400 font-medium max-w-xl`}>
              {t("plan.page_subtitle") ||
                "필요에 맞는 플랜을 선택하세요. 업그레이드하면 더 많은 토큰과 기능을 이용할 수 있습니다."}
            </p>
          </div>
        </div>

        {actionMessage && (
          <div
            role="status"
            className={`rounded-xl border border-seexr-primary/20 bg-seexr-primary/5 font-semibold text-seexr-primary ${embedded ? "px-3 py-2 text-[11px]" : "px-4 py-3 text-[13px]"}`}
          >
            {actionMessage}
          </div>
        )}

        {/* Billing cycle selector is intentionally hidden for the current product UI.
            The existing cycle state remains intact for billing compatibility. */}

        {/* Plans Grid */}
        {loading ? (
          <div
            className={`grid grid-cols-1 ${
              embedded ? "gap-2.5 sm:grid-cols-2" : "gap-5 md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`relative flex flex-col border border-gray-200 bg-white shadow-sm overflow-hidden dark:border-[#2a2a2e] dark:bg-[#161618] ${embedded ? "rounded-xl p-3" : "rounded-[24px] p-6"}`}
              >
                {/* Title + description skeleton */}
                <div className={`flex flex-col ${embedded ? "min-h-[46px] gap-1.5" : "min-h-[72px] gap-2"}`}>
                  <div className={`${embedded ? "h-4 w-16" : "h-6 w-24"} rounded-md bg-gray-200 dark:bg-[#2a2a2e] animate-pulse`} />
                  <div className={`${embedded ? "h-2.5" : "h-3.5"} w-full rounded bg-gray-100 dark:bg-[#1f1f22] animate-pulse`} />
                  <div className={`${embedded ? "h-2.5" : "h-3.5"} w-2/3 rounded bg-gray-100 dark:bg-[#1f1f22] animate-pulse`} />
                </div>

                {/* Price skeleton */}
                <div className={`flex flex-col gap-2 ${embedded ? "my-3 min-h-[48px]" : "mt-5 mb-6 min-h-[72px]"}`}>
                  <div className="h-3 w-16 rounded bg-gray-100 dark:bg-[#1f1f22] animate-pulse" />
                  <div className={`${embedded ? "h-6 w-24" : "h-9 w-36"} rounded-lg bg-gray-200 dark:bg-[#2a2a2e] animate-pulse`} />
                  <div className="h-3 w-24 rounded bg-gray-100 dark:bg-[#1f1f22] animate-pulse" />
                </div>

                {/* Feature list skeleton */}
                <ul className={`flex flex-col ${embedded ? "mb-3 gap-1.5" : "mb-6 gap-2.5"}`}>
                  <li className="h-3.5 w-40 rounded bg-gray-100 dark:bg-[#1f1f22] animate-pulse" />
                </ul>

                {/* CTA skeleton */}
                <div className={`${embedded ? "h-[30px]" : "h-[44px]"} mt-auto rounded-full bg-gray-100 dark:bg-[#1f1f22] animate-pulse`} />
              </div>
            ))}
          </div>
        ) : loadError && plans.length === 0 ? (
          <div className={`flex flex-col items-center justify-center text-center ${embedded ? "py-10" : "py-20"}`}>
            <span className={`max-w-md font-bold text-rose-600 dark:text-rose-300 ${embedded ? "text-[12px]" : "text-[15px]"}`}>
              {loadError}
            </span>
            <button
              type="button"
              onClick={() => {
                setLoading(true);
                setReloadRevision((revision) => revision + 1);
              }}
              className="mt-4 rounded-xl bg-seexr-solid px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-seexr-solid/90"
            >
              다시 시도
            </button>
          </div>
        ) : plans.length === 0 ? (
          <div className={`flex flex-col items-center justify-center text-center ${embedded ? "py-10" : "py-20"}`}>
            <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-[#1f1f22] flex items-center justify-center mb-4 border border-gray-100 dark:border-[#2a2a2e]">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-400"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12h8" />
              </svg>
            </div>
            <span className="text-[15px] font-bold text-gray-700 dark:text-gray-300 mb-1">
              {t("plan.empty_title") || "제공 중인 플랜이 없어요"}
            </span>
            <span className="text-[14px] text-gray-500 dark:text-gray-400 max-w-sm">
              {t("plan.empty_subtitle") || "관리자에게 문의해주세요."}
            </span>
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 ${
              embedded ? "gap-2.5 sm:grid-cols-2" : "gap-5 md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {plans.map((plan) => {
              // 현재 구독 중인 플랜은 월/연 토글 상관없이 양쪽 다 "이용 중" 으로 처리
              const isCurrent = plan.id === currentPlanId;
              const cyclePrice = getPriceFor(plan, cycle);
              const hasCyclePrice = cyclePrice != null && cyclePrice > 0;
              const promo = computePromo(
                plan,
                cycle,
                cyclePrice ?? 0,
                language,
              );
              const savings = cycle === "yr" ? computeYearlySavings(plan) : 0;
              const cycleLabel =
                cycle === "mo"
                  ? t("plan.per_month") || "/월"
                  : t("plan.per_year") || "/연";
              const monthlyEquivalent =
                cycle === "yr" && cyclePrice && cyclePrice > 0
                  ? Math.round(cyclePrice / 12)
                  : null;
              const planName = pickI18n(
                plan.name_i18n ?? null,
                language,
                plan.name,
              );
              const planDesc = pickI18n(
                plan.description_i18n ?? null,
                language,
                plan.description,
              );
              return (
                <div
                  key={plan.id}
                  className={`relative flex flex-col border bg-white dark:bg-[#161618] shadow-sm transition-colors ${embedded ? "rounded-xl p-3" : "rounded-[24px] p-6"} ${
                    isCurrent
                      ? "border-seexr-primary/50 ring-2 ring-seexr-primary/20"
                      : "border-gray-200 dark:border-[#2a2a2e] hover:border-seexr-primary/30"
                  }`}
                >
                  <div className={`absolute flex items-center ${embedded ? "right-3 top-3 gap-1" : "right-4 top-4 gap-1.5"}`}>
                    {promo.active && (
                      <span className={`${embedded ? "px-1.5 text-[8px]" : "px-2 text-[11px]"} py-0.5 rounded-full bg-rose-500 text-white font-black shadow-[0_0_10px_rgba(244,63,94,0.4)]`}>
                        -{promo.discountPercent}%
                      </span>
                    )}
                    {cycle === "yr" && savings > 0 && !promo.active && (
                      <span className={`${embedded ? "px-1.5 text-[8px]" : "px-2 text-[11px]"} py-0.5 rounded-full bg-rose-500 text-white font-black shadow-[0_0_10px_rgba(244,63,94,0.4)]`}>
                        -{savings}%
                      </span>
                    )}
                    {isCurrent && (
                      <span className={`${embedded ? "px-1.5 text-[8px] tracking-wide" : "px-2 text-[10px] tracking-widest"} py-0.5 rounded-full bg-seexr-solid text-white font-black uppercase`}>
                        {t("plan.current_label") || "현재 플랜"}
                      </span>
                    )}
                  </div>

                  <div className={`flex flex-col gap-1 ${embedded ? "min-h-[48px]" : "min-h-[72px]"}`}>
                    <h3 className={`${embedded ? "pr-16 text-[14px]" : "text-[20px]"} font-black text-gray-900 dark:text-gray-100 tracking-tight`}>
                      {planName}
                    </h3>
                    {planDesc && (
                      <p className={`${embedded ? "text-[10px] leading-relaxed" : "text-[13px]"} text-gray-500 dark:text-gray-400 font-medium line-clamp-2`}>
                        {planDesc}
                      </p>
                    )}
                  </div>

                  <div className={`flex flex-col ${embedded ? "my-3 min-h-[50px]" : "mt-5 mb-6 min-h-[72px]"}`}>
                    {!hasCyclePrice && cyclePrice !== 0 ? (
                      <div className={`${embedded ? "text-[11px]" : "text-[14px]"} text-gray-400 dark:text-gray-500 font-semibold`}>
                        {cycle === "yr"
                          ? t("plan.yearly_not_available") || "연간 결제 미제공"
                          : t("plan.monthly_not_available") ||
                            "월간 결제 미제공"}
                      </div>
                    ) : (
                      <>
                        {promo.active && hasCyclePrice && (
                          <>
                            {promo.name && (
                              <span className={`${embedded ? "text-[8px] tracking-wide" : "text-[11px] tracking-widest"} font-black uppercase text-rose-500 dark:text-rose-400 mb-1`}>
                                {promo.name}
                              </span>
                            )}
                            <div className="flex items-baseline gap-2">
                              <span className={`${embedded ? "text-[10px]" : "text-[14px]"} text-gray-400 dark:text-gray-500 line-through tracking-tight`}>
                                ₩{cyclePrice!.toLocaleString()}
                              </span>
                              <span className={`${embedded ? "text-[9px]" : "text-[11px]"} font-black text-rose-500 dark:text-rose-400`}>
                                -{promo.discountPercent}%
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex items-baseline gap-1">
                          <span
                            className={`${embedded ? "text-[21px]" : "text-[32px]"} font-black tracking-tight ${promo.active ? "text-seexr-primary" : "text-gray-900 dark:text-gray-100"}`}
                          >
                            {cyclePrice === 0
                              ? t("plan.free") || "무료"
                              : `₩${(promo.active ? promo.discountedPrice : (cyclePrice ?? 0)).toLocaleString()}`}
                          </span>
                          {hasCyclePrice && (
                            <span className={`${embedded ? "text-[10px]" : "text-[14px]"} font-bold text-gray-400 dark:text-gray-500`}>
                              {cycleLabel}
                            </span>
                          )}
                        </div>
                        {cycle === "yr" &&
                          monthlyEquivalent != null &&
                          !promo.active && (
                            <span className={`${embedded ? "text-[9px]" : "text-[12px]"} text-gray-500 dark:text-gray-400 font-medium mt-1`}>
                              ≈ ₩{monthlyEquivalent.toLocaleString()}{" "}
                              {t("plan.per_month") || "/월"}
                              {savings > 0 && (
                                <>
                                  {" "}
                                  ·{" "}
                                  <span className="font-bold text-rose-500 dark:text-rose-400">
                                    {savings}% 할인
                                  </span>
                                </>
                              )}
                            </span>
                          )}
                      </>
                    )}
                  </div>

                  <ul className={`flex flex-col ${embedded ? "mb-3 gap-1.5" : "mb-6 gap-2.5"}`}>
                    <li className={`flex items-center text-gray-700 dark:text-gray-300 font-medium ${embedded ? "gap-1.5 text-[10px]" : "gap-2 text-[13px]"}`}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className={`${embedded ? "h-3 w-3" : "h-4 w-4"} text-seexr-primary shrink-0`}
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>
                        {plan.max_tokens == null
                          ? "AI 토큰 무제한"
                          : (
                              t("plan.tokens_per_month") || "월 {{n}} 토큰"
                            ).replace(
                              "{{n}}",
                              plan.max_tokens.toLocaleString(),
                            )}
                      </span>
                    </li>
                    <li className={`flex items-center text-gray-700 dark:text-gray-300 font-medium ${embedded ? "gap-1.5 text-[10px]" : "gap-2 text-[13px]"}`}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className={`${embedded ? "h-3 w-3" : "h-4 w-4"} text-seexr-primary shrink-0`}
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>
                        스토리지{" "}
                        {plan.storage_limit_bytes == null
                          ? "무제한"
                          : `${(plan.storage_limit_bytes / 1073741824).toLocaleString()}GB`}
                      </span>
                    </li>
                    <li className={`flex items-center text-gray-700 dark:text-gray-300 font-medium ${embedded ? "gap-1.5 text-[10px]" : "gap-2 text-[13px]"}`}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className={`${embedded ? "h-3 w-3" : "h-4 w-4"} text-seexr-primary shrink-0`}
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>
                        SEEXR Platform{" "}
                        {plan.paas_enabled ? "사용 가능" : "미포함"}
                      </span>
                    </li>
                    {Number(plan.promotion_benefits?.aiTokensBonus ?? 0) >
                      0 && (
                      <li className={`flex items-center font-semibold text-rose-500 ${embedded ? "gap-1.5 text-[10px]" : "gap-2 text-[13px]"}`}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          className={`${embedded ? "h-3 w-3" : "h-4 w-4"} shrink-0`}
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        이벤트 토큰 +
                        {Number(
                          plan.promotion_benefits?.aiTokensBonus,
                        ).toLocaleString()}
                      </li>
                    )}
                    {Number(plan.promotion_benefits?.storageBytesBonus ?? 0) >
                      0 && (
                      <li className={`flex items-center font-semibold text-rose-500 ${embedded ? "gap-1.5 text-[10px]" : "gap-2 text-[13px]"}`}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          className={`${embedded ? "h-3 w-3" : "h-4 w-4"} shrink-0`}
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        이벤트 스토리지 +
                        {(
                          Number(plan.promotion_benefits?.storageBytesBonus) /
                          1073741824
                        ).toLocaleString()}
                        GB
                      </li>
                    )}
                    {plan.promotion_benefits?.paasEnabled &&
                      !plan.paas_enabled && (
                        <li className={`flex items-center font-semibold text-rose-500 ${embedded ? "gap-1.5 text-[10px]" : "gap-2 text-[13px]"}`}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            className={`${embedded ? "h-3 w-3" : "h-4 w-4"} shrink-0`}
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          이벤트 기간 Platform 사용 가능
                        </li>
                      )}
                  </ul>

                  <div className="mt-auto">
                    {isCurrent ? (
                      <button
                        disabled
                        className={`${embedded ? "h-[30px] text-[10px]" : "h-[44px] text-[13px]"} w-full rounded-full bg-gray-100 dark:bg-[#1f1f22] text-gray-400 dark:text-gray-500 font-bold cursor-not-allowed`}
                      >
                        {t("plan.current_button") || "현재 이용 중"}
                      </button>
                    ) : !hasCyclePrice && cyclePrice !== 0 ? (
                      <button
                        disabled
                        className={`${embedded ? "h-[30px] text-[10px]" : "h-[44px] text-[13px]"} w-full rounded-full bg-gray-100 dark:bg-[#1f1f22] text-gray-400 dark:text-gray-500 font-bold cursor-not-allowed`}
                      >
                        {t("plan.unavailable_cycle") || "이 주기 미제공"}
                      </button>
                    ) : cyclePrice !== 0 &&
                      (!plan.billingAvailable ||
                        !planInfo?.billingAvailable) ? (
                      <button
                        disabled
                        className={`${embedded ? "h-[30px] text-[10px]" : "h-[44px] text-[13px]"} w-full rounded-full bg-gray-100 dark:bg-[#1f1f22] text-gray-400 dark:text-gray-500 font-bold cursor-not-allowed`}
                      >
                        결제 준비 중
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSelect(plan)}
                        disabled={changingPlanId === plan.id}
                        className={`${embedded ? "h-[30px] text-[10px]" : "h-[44px] text-[13px]"} w-full rounded-full bg-seexr-solid text-white font-bold hover:bg-seexr-hover transition-all shadow-[0_0_15px_rgb(var(--seexr-rgb-500)/0.3)] hover:shadow-[0_0_20px_rgb(var(--seexr-rgb-500)/0.5)]`}
                      >
                        {changingPlanId === plan.id
                          ? "처리 중..."
                          : cyclePrice === 0 && planInfo?.subscription_status
                            ? "기간 종료 후 무료 전환"
                            : t("plan.select") || "선택"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
